import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Package,
  Truck,
  MagnifyingGlass,
  DownloadSimple,
  FloppyDisk,
  CaretDown,
  CaretRight,
  TrendUp,
  TrendDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import NumberInput from "@/components/ui/NumberInput";

const fmtMoney = (n) => `$ ${Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtNum = (n, d = 3) => Number(n || 0).toLocaleString("es-AR", { minimumFractionDigits: 0, maximumFractionDigits: d });
const fmtFecha = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = (iso || "").split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
};

export default function InsumosCompradosCard({ fechaDesde, fechaHasta }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [agrupar, setAgrupar] = useState("insumo"); // "insumo" | "proveedor"
  const [nuevoCosto, setNuevoCosto] = useState({}); // {insumo_id: "1234.56"}
  const [saving, setSaving] = useState({});
  const [openProv, setOpenProv] = useState({}); // proveedor_id → bool
  const [visibleCount, setVisibleCount] = useState(10);

  const cargar = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/insumos-comprados", {
        params: { fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
      });
      setData(data);
      setNuevoCosto({});
    } catch (_) { setData(null); }
    finally { setLoading(false); }
  }, [fechaDesde, fechaHasta]);
  useEffect(() => { cargar(); }, [cargar]);

  const items = data?.insumos || [];
  const proveedores = data?.proveedores || [];

  const filteredInsumos = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((i) =>
      (i.nombre || "").toLowerCase().includes(s) ||
      (i.codigo || "").toLowerCase().includes(s) ||
      (i.proveedores || []).some((p) => (p.proveedor_nombre || "").toLowerCase().includes(s))
    );
  }, [items, q]);
  const filteredProveedores = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return proveedores;
    return proveedores.filter((p) =>
      (p.proveedor_nombre || "").toLowerCase().includes(s) ||
      (p.insumos || []).some((i) => (i.nombre || "").toLowerCase().includes(s))
    );
  }, [proveedores, q]);

  useEffect(() => { setVisibleCount(10); }, [q, agrupar, items.length]);

  const visibleInsumos = visibleCount >= filteredInsumos.length ? filteredInsumos : filteredInsumos.slice(0, visibleCount);
  const visibleProveedores = visibleCount >= filteredProveedores.length ? filteredProveedores : filteredProveedores.slice(0, visibleCount);

  const calcDiff = (i) => {
    const nc = parseFloat(nuevoCosto[i.insumo_id]);
    if (!Number.isFinite(nc)) return null;
    const diff = nc - (i.costo_actual || 0);
    const pct = i.costo_actual > 0 ? (diff / i.costo_actual * 100) : null;
    return { nuevo: nc, diff, pct };
  };

  const guardarCosto = async (i) => {
    const nc = parseFloat(nuevoCosto[i.insumo_id]);
    if (!Number.isFinite(nc) || nc < 0) return toast.warning("Ingresá un costo válido");
    setSaving((s) => ({ ...s, [i.insumo_id]: true }));
    try {
      await api.put(`/dashboard/insumos-comprados/${i.insumo_id}/costo`, { ultimo_costo: nc });
      toast.success(`Costo actualizado a ${fmtMoney(nc)} · ${i.nombre}`);
      setNuevoCosto((s) => { const c = { ...s }; delete c[i.insumo_id]; return c; });
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally { setSaving((s) => ({ ...s, [i.insumo_id]: false })); }
  };

  const exportarCsv = () => {
    const rows = [
      ["Insumo", "Código", "Unidad", "Cantidad", "Facturado", "Costo actual", "Costo prom período", "Proveedores"],
      ...filteredInsumos.map((i) => [
        i.nombre, i.codigo || "", i.unidad, i.cantidad_total, i.facturado,
        i.costo_actual, i.costo_prom_periodo,
        (i.proveedores || []).map((p) => `${p.proveedor_nombre} (${p.cantidad}×${p.veces})`).join(" | "),
      ]),
    ];
    const csv = rows.map(r => r.map(c => `"${String(c).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `insumos_comprados_${data?.desde}_${data?.hasta}.csv`; a.click();
  };

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="dash-insumos-comprados">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading font-semibold text-[15px] flex items-center gap-2">
            <Package size={16} weight="bold" className="text-[#6B1F24]" />
            Insumos Comprados · Actualizar Costos
          </h2>
          <div className="text-[11px] text-[#666] font-mono mt-0.5">
            {data ? (
              <>
                {data.total_insumos} insumo{data.total_insumos === 1 ? "" : "s"} · {data.total_proveedores} proveedor{data.total_proveedores === 1 ? "" : "es"} · {fmtMoney(data.total_facturado)}
              </>
            ) : "Compras del período por insumo · costo actual del sistema"}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              placeholder="Buscar insumo o proveedor…" value={q} onChange={(e) => setQ(e.target.value)}
              className="h-8 pl-7 w-56 text-[12px]"
              data-testid="insumos-comp-search"
            />
          </div>
          <div className="inline-flex rounded-sm overflow-hidden border border-neutral-300" data-testid="insumos-comp-agrupar">
            <button
              type="button"
              onClick={() => setAgrupar("insumo")}
              className={`px-3 h-8 text-[11px] font-mono uppercase transition-colors flex items-center gap-1 ${agrupar === "insumo" ? "bg-[#6B1F24] text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}
              data-testid="insumos-comp-agrupar-insumo"
            >
              <Package size={11} weight="bold" /> Por insumo
            </button>
            <button
              type="button"
              onClick={() => setAgrupar("proveedor")}
              className={`px-3 h-8 text-[11px] font-mono uppercase transition-colors flex items-center gap-1 border-l border-neutral-300 ${agrupar === "proveedor" ? "bg-[#6B1F24] text-white" : "bg-white text-neutral-600 hover:bg-neutral-50"}`}
              data-testid="insumos-comp-agrupar-proveedor"
            >
              <Truck size={11} weight="bold" /> Por proveedor
            </button>
          </div>
          <Button variant="outline" onClick={exportarCsv} className="h-8 rounded-sm text-[12px]" disabled={filteredInsumos.length === 0}
                  data-testid="insumos-comp-export">
            <DownloadSimple size={13} className="mr-1" /> CSV
          </Button>
        </div>
      </div>

      {loading && <div className="p-8 text-center text-neutral-500 text-sm">Cargando…</div>}
      {!loading && (!data || items.length === 0) && (
        <div className="p-8 text-center text-neutral-400 text-sm">
          Sin compras de insumos en el período seleccionado.
        </div>
      )}

      {/* Vista Por Insumo */}
      {!loading && agrupar === "insumo" && filteredInsumos.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-neutral-50 text-[10px] uppercase font-mono text-neutral-600 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left">Insumo</th>
                <th className="px-3 py-2 text-right w-28">Cantidad</th>
                <th className="px-3 py-2 text-right w-28">Facturado</th>
                <th className="px-3 py-2 text-right w-28">Costo prom período</th>
                <th className="px-3 py-2 text-right w-28">Costo actual</th>
                <th className="px-3 py-2 text-left w-64">Proveedores</th>
                <th className="px-3 py-2 text-right w-28">Nuevo costo</th>
                <th className="px-3 py-2 text-right w-28">Diferencia</th>
                <th className="px-3 py-2 text-center w-14"></th>
              </tr>
            </thead>
            <tbody>
              {visibleInsumos.map((i, idx) => {
                const diff = calcDiff(i);
                const isSaving = !!saving[i.insumo_id];
                return (
                  <tr key={i.insumo_id} className="border-t border-neutral-100 hover:bg-neutral-50/60"
                      data-testid={`insumos-comp-row-${idx}`}>
                    <td className="px-3 py-1.5">
                      <div className="font-medium truncate max-w-[240px]" title={i.nombre}>{i.nombre}</div>
                      <div className="text-[10px] font-mono text-neutral-400">
                        {i.codigo && <>{i.codigo} · </>}Stock: {fmtNum(i.stock_actual)} {i.unidad} · {i.veces} compra{i.veces === 1 ? "" : "s"}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {fmtNum(i.cantidad_total)}
                      <span className="text-[10px] text-neutral-400 ml-0.5">{i.unidad}</span>
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold">{fmtMoney(i.facturado)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtMoney(i.costo_prom_periodo)}</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold text-neutral-800">{fmtMoney(i.costo_actual)}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-1">
                        {(i.proveedores || []).slice(0, 3).map((p, j) => (
                          <span key={j} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono bg-neutral-100 border border-neutral-200 text-neutral-700"
                                title={`${p.proveedor_nombre} · ${fmtNum(p.cantidad)} ${i.unidad} · ${fmtMoney(p.facturado)} · última: ${fmtFecha(p.ultima_compra)}`}>
                            <Truck size={9} weight="bold" className="text-neutral-400" />
                            <span className="truncate max-w-[110px]">{p.proveedor_nombre}</span>
                          </span>
                        ))}
                        {(i.proveedores || []).length > 3 && (
                          <span className="text-[10px] text-neutral-400 font-mono">+{i.proveedores.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <NumberInput step="0.01" min="0"
                        value={nuevoCosto[i.insumo_id] ?? ""}
                        onChange={(e) => setNuevoCosto(s => ({ ...s, [i.insumo_id]: e.target.value }))}
                        placeholder={String(i.costo_actual)}
                        className="h-7 w-full text-[11px] font-mono text-right"
                        data-testid={`insumos-comp-input-${idx}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">
                      {diff ? (
                        <div>
                          <div className={diff.diff > 0 ? "text-rose-700 font-bold" : diff.diff < 0 ? "text-emerald-700 font-bold" : "text-neutral-500"}>
                            {diff.diff > 0 ? "+" : ""}{fmtMoney(diff.diff)}
                          </div>
                          {diff.pct != null && (
                            <div className={`text-[10px] flex items-center justify-end gap-0.5 ${diff.diff > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                              {diff.diff > 0 ? <TrendUp size={9} weight="bold"/> : <TrendDown size={9} weight="bold"/>}
                              {diff.pct > 0 ? "+" : ""}{diff.pct.toFixed(1)}%
                            </div>
                          )}
                        </div>
                      ) : <span className="text-neutral-300">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => guardarCosto(i)}
                        disabled={!diff || isSaving}
                        className="h-7 px-2 text-emerald-700 hover:bg-emerald-50 disabled:opacity-30"
                        title="Guardar nuevo costo"
                        data-testid={`insumos-comp-save-${idx}`}
                      >
                        <FloppyDisk size={13} weight="bold" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredInsumos.length > 10 && (
            <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="insumos-comp-paginacion">
              <div className="text-[11px] font-mono text-neutral-600">
                Mostrando <span className="font-bold text-neutral-800">{visibleInsumos.length}</span> de{" "}
                <span className="font-bold text-neutral-800">{filteredInsumos.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={visibleCount >= filteredInsumos.length ? "all" : String(visibleCount)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVisibleCount(v === "all" ? filteredInsumos.length : parseInt(v, 10));
                  }}
                  className="h-8 text-[12px] font-mono border border-neutral-300 rounded-sm px-2 bg-white focus:outline-none focus:border-[#6B1F24]"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="all">Todos</option>
                </select>
                {visibleCount < filteredInsumos.length && (
                  <Button size="sm" variant="outline" onClick={() => setVisibleCount((v) => Math.min(filteredInsumos.length, v + 10))} className="h-8 rounded-sm text-[11px]">
                    Ver 10 más
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Vista Por Proveedor */}
      {!loading && agrupar === "proveedor" && filteredProveedores.length > 0 && (
        <div className="p-3">
          {visibleProveedores.map((p, idx) => {
            const pk = p.proveedor_id || `noid::${p.proveedor_nombre}`;
            const open = !!openProv[pk];
            return (
              <div key={pk} className="border border-neutral-200 rounded-sm mb-2 bg-white"
                   data-testid={`insumos-comp-prov-${idx}`}>
                <button
                  type="button"
                  onClick={() => setOpenProv((s) => ({ ...s, [pk]: !s[pk] }))}
                  className="w-full px-4 py-2 border-b border-neutral-100 bg-neutral-50 flex items-center gap-2 hover:bg-neutral-100 text-left"
                >
                  {open ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                  <Truck size={13} weight="duotone" className="text-[#6B1F24]" />
                  <span className="font-heading font-semibold text-[13px] text-[#6B1F24] flex-1">{p.proveedor_nombre}</span>
                  <span className="text-[11px] font-mono text-neutral-500">
                    {p.insumos.length} insumo{p.insumos.length === 1 ? "" : "s"} · {p.total_veces} compra{p.total_veces === 1 ? "" : "s"}
                  </span>
                  <span className="text-[13px] font-mono font-bold text-neutral-800 min-w-[110px] text-right">
                    {fmtMoney(p.total_facturado)}
                  </span>
                </button>
                {open && (
                  <table className="w-full text-[12px]">
                    <thead className="bg-neutral-50/70 text-[10px] uppercase font-mono text-neutral-600">
                      <tr>
                        <th className="px-3 py-1.5 text-left">Insumo</th>
                        <th className="px-3 py-1.5 text-right w-24">Cantidad</th>
                        <th className="px-3 py-1.5 text-right w-24">Facturado</th>
                        <th className="px-3 py-1.5 text-right w-24">Costo actual</th>
                        <th className="px-3 py-1.5 text-right w-24">Última compra</th>
                        <th className="px-3 py-1.5 text-center w-14">Veces</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.insumos.map((i, j) => (
                        <tr key={j} className="border-t border-neutral-100 hover:bg-neutral-50/40"
                            data-testid={`insumos-comp-prov-${idx}-item-${j}`}>
                          <td className="px-3 py-1 truncate max-w-[280px]" title={i.nombre}>
                            {i.nombre}
                            {i.codigo && <span className="text-[10px] text-neutral-400 font-mono ml-2">{i.codigo}</span>}
                          </td>
                          <td className="px-3 py-1 text-right font-mono">
                            {fmtNum(i.cantidad)}
                            <span className="text-[10px] text-neutral-400 ml-0.5">{i.unidad}</span>
                          </td>
                          <td className="px-3 py-1 text-right font-mono font-bold">{fmtMoney(i.facturado)}</td>
                          <td className="px-3 py-1 text-right font-mono">{fmtMoney(i.costo_actual)}</td>
                          <td className="px-3 py-1 text-right font-mono text-neutral-500">{fmtFecha(i.ultima_compra)}</td>
                          <td className="px-3 py-1 text-center font-mono">{i.veces}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
          {filteredProveedores.length > 10 && (
            <div className="px-2 py-3 flex items-center justify-between gap-3 flex-wrap"
                 data-testid="insumos-comp-prov-paginacion">
              <div className="text-[11px] font-mono text-neutral-600">
                Mostrando <span className="font-bold text-neutral-800">{visibleProveedores.length}</span> de{" "}
                <span className="font-bold text-neutral-800">{filteredProveedores.length}</span> proveedores
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={visibleCount >= filteredProveedores.length ? "all" : String(visibleCount)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setVisibleCount(v === "all" ? filteredProveedores.length : parseInt(v, 10));
                  }}
                  className="h-8 text-[12px] font-mono border border-neutral-300 rounded-sm px-2 bg-white focus:outline-none focus:border-[#6B1F24]"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="all">Todos</option>
                </select>
                {visibleCount < filteredProveedores.length && (
                  <Button size="sm" variant="outline" onClick={() => setVisibleCount((v) => Math.min(filteredProveedores.length, v + 10))} className="h-8 rounded-sm text-[11px]">
                    Ver 10 más
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
